import { Component, computed, inject, LOCALE_ID, signal } from '@angular/core';
import { Pagination } from '../../../../shared/ui/shared-tables/pagination/pagination';
import { Tables } from '../../../../shared/ui/shared-tables/tables/tables';
import { ErrorData } from '../../../../utils/error-data/error-data';
import { Loading } from '../../../../utils/loading/loading';
import { Router } from '@angular/router';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';
import { Bank, BankAccount } from '../../../../pages/admin-dashboard/bank-account/bank-account.model';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
@Component({
  selector: 'app-bank-accounts-list',
  imports: [Pagination, Tables, ErrorData, Loading, SuccessfulNotification, ConfirmationDialog],
  templateUrl: './bank-accounts-list.html',
  styleUrl: './bank-accounts-list.scss',
})
export class BankAccountsList {
  private locale = inject(LOCALE_ID);
  private router = inject(Router);
  isArabic = computed(() => this.locale.startsWith('ar'));
  loading = signal(false);
  errorMessage = signal<string | null>(null);
  successMessage = signal<string | null>(null);
  bankLabel = $localize`:@@bankAccountBank:Bank`;
  accountNumberLabel = $localize`:@@bankAccountNumber:Account Number`;
  ibanLabel = $localize`:@@bankAccountIban:IBAN`;
  deleteTitle = $localize`:@@deleteBankAccountTitle:Delete Bank Account`;
  deleteDescription = $localize`:@@deleteBankAccountDescription:Are you sure you want to delete this bank account?`;
  confirmButton = $localize`:@@adminDeleteDialogConfirm:Delete`;
  cancelButton = $localize`:@@ppReasonsEditCancelButton:Cancel`;
  deleteErrorMessage = signal('');
  createdOnByLabel = $localize`:@@instapayCreatedOnBy:Created On / By`;
  headerItems = computed(() => [
    { key: 'bank', label: this.bankLabel },
    { key: 'accountNumber', label: this.accountNumberLabel },
    { key: 'iban', label: this.ibanLabel },
    { key: 'createdOnBy', label: this.createdOnByLabel },
    { key: 'actions', label: '', type: 'actions' },
  ]);

  showDeleteDialog = signal(false);
  onCancelDelete() {
    this.showDeleteDialog.set(false);
  }
  onConfirmDelete() {
    console.log('nk');
  }

  bankAccounts = signal<BankAccount[]>([
    {
      id: 1,
      bank: {
        id: 1,
        titleEn: 'CIB',
        titleAr: 'البنك التجاري الدولي',
      },
      accountNumber: '123456789012',
      iban: 'EG380019000500000000263180002',
      createdOn: '10/08/2026 09:45 AM',
      createdByEn: 'Mohamed Ali',
      createdByAr: 'محمد علي',
    },
    {
      id: 2,
      bank: {
        id: 2,
        titleEn: 'National Bank of Egypt',
        titleAr: 'البنك الأهلي المصري',
      },
      accountNumber: '987654321098',
      iban: 'EG170001000100000012345678901',
      createdOn: '15/07/2026 02:30 PM',
      createdByEn: 'Sara Hassan',
      createdByAr: 'سارة حسن',
    },
    {
      id: 3,
      bank: {
        id: 3,
        titleEn: 'National Bank of Egypt',
        titleAr: 'البنك الأهلي المصري',
      },
      accountNumber: '987654321098',
      iban: 'EG170001000100000012345678901',
      createdOn: '15/07/2026 02:30 PM',
      createdByEn: 'Sara Hassan',
      createdByAr: 'سارة حسن',
    },
  ]);
  tableRows = computed(() => {
    return this.bankAccounts().map((account) => ({
      id: account.id,
      bank: this.isArabic() ? account.bank.titleAr : account.bank.titleEn,
      accountNumber: account.accountNumber,
      iban: account.iban,

      createdOn: account.createdOn,
      createdBy: this.isArabic() ? account.createdByAr : account.createdByEn,

      rawBank: this.isArabic() ? account.bank.titleAr : account.bank.titleEn,
      rawAccountNumber: account.accountNumber,
      rawIban: account.iban,
    }));
  });
  actions: Bank[] = [
    { id: 1, titleAr: 'نسخ', titleEn: 'Copy' },
    { id: 2, titleAr: 'حذف', titleEn: 'Delete' },
  ];
  async handleAction(event: any) {
    const row = event.entity;
    if (event.actionId === 1) {
      try {
        const textToCopy = `${row.rawBank} - ${row.rawAccountNumber} - ${row.rawIban}`;
        await navigator.clipboard.writeText(textToCopy);
        this.successMessage.set(
          $localize`:@@bankAccountDetailsCopiedSuccessfully:Bank account details copied successfully`,
        );
        setTimeout(() => {
          this.successMessage.set(null);
        }, 3000);
      } catch (error) {
        console.error('Failed to copy', error);
      }
    } else if (event.actionId === 2) {
      this.showDeleteDialog.set(true);
    }
  }
  currentPage = signal(1);
  itemsPerPage = signal(25);
  totalItems = computed(() => this.tableRows().length);
  pagedRows = computed(() => {
    const start = (this.currentPage() - 1) * this.itemsPerPage();
    const end = start + this.itemsPerPage();
    return this.tableRows().slice(start, end);
  });
  totalPages = computed(() => Math.ceil(this.totalItems() / this.itemsPerPage()));
  startItem = computed(() => (this.totalItems() ? (this.currentPage() - 1) * this.itemsPerPage() + 1 : 0));
  endItem = computed(() => Math.min(this.currentPage() * this.itemsPerPage(), this.totalItems()));
  getPageNumbers() {
    const pages: number[] = [];
    for (let i = 1; i <= this.totalPages(); i++) {
      pages.push(i);
    }
    return pages;
  }
  onPreviousPage() {
    if (this.currentPage() > 1) {
      this.currentPage.update((page) => page - 1);
    }
  }
  onNextPage() {
    if (this.currentPage() < this.totalPages()) {
      this.currentPage.update((page) => page + 1);
    }
  }
  onGoToPage(page: number) {
    this.currentPage.set(page);
  }
  onChangePageNumber(size: number) {
    this.itemsPerPage.set(+size);
    this.currentPage.set(1);
  }
  onPageSizeChange() {
    this.currentPage.set(1);
  }
}
